import React, { useState } from 'react';
import { AlertCircle, Bot, BookOpen, CheckCircle, XCircle, Flag } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../../supabase/client';
import { getImageUrl } from '../../../utils/questionHtml';
import QuestionHtmlWithZoom from '../../../components/QuestionHtmlWithZoom';
import GraphicsAnswerUpload from '../../../components/GraphicsAnswerUpload';
import ReportErrorModal from '../../../components/ReportErrorModal';
import CodingFileUpload from '../../../components/CodingFileUpload';
// Scratch components
const ScratchCodingAnswerArea = ({ question, paperId, isSubmitted, answer, onAnswerChange }: any) => (
  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
    <p className="text-gray-500 text-sm">Scratch 编辑器已移除，请使用 Python 或 C++ 编程环境</p>
  </div>
);
import ScratchResultDetail from '../../../components/ScratchResultDetail';
import PythonCodingAnswerArea from '../../../components/PythonCodingAnswerArea';
import PythonCodingResultDetail from '../../../components/PythonCodingResultDetail';
import CppCodingAnswerArea from '../../../components/CppCodingAnswerArea';
import CppCodingResultDetail from '../../../components/CppCodingResultDetail';
import type { QuestionRendererProps, Question } from '../types';
import type { GraphicsQuestion } from '../../../types/graphics';

// 判断是否为客观题
const isObjectiveQuestion = (type: string) => {
  return type === 'single_choice' || type === 'multi_choice' || type === 'judge';
};

// 兼容 question_feedbacks 的两种格式：
// 新格式：{score, feedback} 对象（教师阅卷后存储）
// 旧格式：纯字符串（仅评语，无评分）
const normalizeFeedback = (raw: any): { score?: number; feedback?: string } | undefined => {
  if (!raw) return undefined;
  if (typeof raw === 'object' && raw !== null) {
    return { score: raw.score, feedback: raw.feedback };
  }
  // 旧格式：纯字符串评语，没有 score
  return { feedback: String(raw) };
};

// 判断当前题目是否需要禁用（仅刷题平台受AI阅卷开关影响）
const shouldDisableQuestion = (
  questionType: string,
  aiGradingEnabled: boolean,
  isAssignmentMode: boolean
) => {
  // 我的作业：始终可以作答，不受AI阅卷开关影响
  if (isAssignmentMode) return false;
  // 刷题平台：AI阅卷关闭时，非客观题不可作答
  if (!aiGradingEnabled) return !isObjectiveQuestion(questionType);
  return false;
};

const GraphicsAnswerArea = ({ question, paperId, currentUser }: { question: Question; paperId?: string; currentUser?: any }) => {
  const submitted = false;
  const graphicsQ: GraphicsQuestion = {
    id: question.id,
    type: 'graphics',
    title: question.title,
    content: '',
    score: question.score,
    difficulty: (question.graphicsDifficulty as 'easy' | 'medium' | 'hard') || 'medium',
    category: question.graphicsCategory || '',
    answerItems: question.graphicsAnswerItems || [{ id: '1', order: 1, name: '作答项1', placeholder: '请上传程序截图', required: true, maxSizeMB: 5, maxCount: 1, maxBase64SizeKB: 500 }],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  return (
    <div className="space-y-4">
      <GraphicsAnswerUpload question={graphicsQ} examId={paperId || 'default'} questionId={question.id} userId={currentUser?.id || ''} disabled={submitted} />
    </div>
  );
};

export default function QuestionRenderer({
  question,
  currentIndex,
  answers,
  isSubmitted,
  isPreview,
  paperId,
  paper,
  currentUser,
  aiExplanations,
  examQuestions,
  aiGradingEnabled,
  isAssignmentMode,
  answerMode = 'online',
  questionFeedbacks,
  revealedQuestions,
  isPracticeMode,
  onAnswerChange,
  onTextAnswerChange,
  onAIGrading,
  onExamQuestionsChange,
  onHasWidthChangesChange
}: QuestionRendererProps) {
  const isDisabled = shouldDisableQuestion(question?.type || '', aiGradingEnabled, isAssignmentMode);
  const [showReportModal, setShowReportModal] = useState(false);

  // 练习模式下使用逐题揭示，否则使用全局 isSubmitted
  const isRevealed = isPracticeMode ? (revealedQuestions?.has(question?.id || '') ?? false) : isSubmitted;

  const handleAnswer = (optionId: string) => {
    if (isRevealed) return;
    if (!question) return;
    const isMultiChoice = question.type === 'multi_choice';
    if (isMultiChoice) {
      const currentAnswers = answers[question.id] ? answers[question.id].split(',') : [];
      const optionIndex = currentAnswers.indexOf(optionId);
      let newAnswersArray: string[];
      if (optionIndex > -1) {
        newAnswersArray = currentAnswers.filter(id => id !== optionId);
      } else {
        newAnswersArray = [...currentAnswers, optionId].sort();
      }
      const newAnswersValue = newAnswersArray.length > 0 ? newAnswersArray.join(',') : '';
      if (newAnswersValue) {
        onAnswerChange(question.id, newAnswersValue);
      } else {
        onAnswerChange(question.id, '');
      }
    } else {
      onAnswerChange(question.id, optionId);
    }
  };

  const handleTextAnswer = (text: string) => {
    if (isRevealed) return;
    if (text.trim()) {
      onTextAnswerChange(question.id, text);
    } else {
      onTextAnswerChange(question.id, '');
    }
  };

  const updateQuestionLayout = (newLayout: string) => {
    if (!onExamQuestionsChange) return;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, titleImageLayout: newLayout } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  const updateOptionLayout = (newLayout: string) => {
    if (!onExamQuestionsChange) return;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, optionLayout: newLayout } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  const updateOptionImageWidth = async (optionId: string, width: number) => {
    if (!onExamQuestionsChange) return;
    const newOptions = question.options.map(o =>
      o.id === optionId ? { ...o, imageWidth: width } : o
    );
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, options: newOptions } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);

    // 预览模式下立即保存到数据库
    if (isPreview && question.id) {
      const optionImageWidths = newOptions.reduce((acc: Record<string, number>, o: any) => {
        if (o.imageWidth) acc[o.id] = o.imageWidth;
        return acc;
      }, {});
      await supabase.from('questions').update({
        option_image_widths: optionImageWidths
      }).eq('id', question.id);
    }
  };

  const updateTitleImageWidth = (idx: number, width: number) => {
    if (!onExamQuestionsChange) return;
    const newWidths = [...(question.titleImageWidths || question.titleImages?.map(() => 100) || [])];
    newWidths[idx] = width;
    const newQuestions = examQuestions.map(q =>
      q.id === question.id ? { ...q, titleImageWidths: newWidths } : q
    );
    onExamQuestionsChange(newQuestions);
    if (onHasWidthChangesChange) onHasWidthChangesChange(true);
  };

  // AI阅卷关闭时刷题平台非客观题禁用提示
  if (isDisabled) {
    return (
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">自动判分功能维护中</h4>
            <p className="text-sm text-amber-700 mt-1">
              试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 图形化题目
  if (question?.type === 'graphics') {
    return <GraphicsAnswerArea question={question} paperId={paperId} currentUser={currentUser} />;
  }

  // Scratch 编程题
  if (question?.type === 'scratch_coding') {
    if (isRevealed) {
      return (
        <ScratchResultDetail
          question={question}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return answerMode === 'upload' ? (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="scratch_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    ) : (
      <ScratchCodingAnswerArea
        question={question}
        paperId={paperId || 'custom'}
        isSubmitted={isRevealed}
        answer={answers[question.id]}
        onAnswerChange={(answer) => {
          if (isRevealed) return;
          if (answer) {
            onAnswerChange(question.id, answer);
          } else {
            onAnswerChange(question.id, '');
          }
        }}
      />
    );
  }

  // Python 编程题
  if (question?.type === 'python_coding') {
    if (isRevealed) {
      return (
        <PythonCodingResultDetail
          question={{
            id: question.id,
            type: question.type,
            title: question.title,
            score: question.score,
            userAnswer: answers[question.id],
          }}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return answerMode === 'upload' ? (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="python_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    ) : (
      <PythonCodingAnswerArea
        question={{
          id: question.id,
          title: question.title,
          content: question.explanation || question.title,
          starterCode: question.codeTemplate,
          referenceSolution: question.referenceAnswer,
          timeLimitMs: 1000,
          memoryLimitMb: 64,
          score: question.score,
        }}
        paperId={paperId || 'custom'}
        isSubmitted={isRevealed}
        answer={answers[question.id]}
        onAnswerChange={(answer) => {
          if (isRevealed) return;
          if (answer) {
            onAnswerChange(question.id, answer);
          } else {
            onAnswerChange(question.id, '');
          }
        }}
      />
    );
  }

  // C++ 编程题
  if (question?.type === 'cpp_coding') {
    if (isRevealed) {
      return (
        <CppCodingResultDetail
          question={{
            id: question.id,
            type: question.type,
            title: question.title,
            score: question.score,
            userAnswer: answers[question.id],
          }}
          paperId={paperId || 'custom'}
          answer={answers[question.id] || ''}
          teacherFeedback={normalizeFeedback(questionFeedbacks?.[question.id])}
        />
      );
    }
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return answerMode === 'upload' ? (
      <CodingFileUpload
        key={question.id}
        questionId={question.id}
        paperId={paperId}
        paperTitle={paper?.title}
        questionTitle={question.title}
        userId={currentUser?.id}
        questionType="cpp_coding"
        onFileUpload={(fileUrl) => onAnswerChange(question.id, fileUrl)}
        disabled={isRevealed}
        existingFileUrl={answers[question.id]}
      />
    ) : (
      <CppCodingAnswerArea
        question={{
          id: question.id,
          title: question.title,
          content: question.explanation || question.title,
          starterCode: question.codeTemplate,
          referenceSolution: question.referenceAnswer,
          timeLimitMs: 1000,
          memoryLimitMb: 64,
          score: question.score,
        }}
        paperId={paperId || 'custom'}
        isSubmitted={isRevealed}
        answer={answers[question.id]}
        onAnswerChange={handleTextAnswer}
      />
    );
  }

  // 主观题/代码题
  if (question?.type === 'coding' || question?.type === 'subjective') {
    if (isDisabled) {
      return (
        <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">自动判分功能维护中</p>
              <p className="text-sm text-amber-700 mt-1">
                试卷提交后会自动显示参考答案和评分规则，请自行对照评分！
              </p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <textarea
          value={answers[question.id] || ''}
          onChange={(e) => handleTextAnswer(e.target.value)}
          disabled={isRevealed}
          placeholder="请输入代码..."
          className="w-full h-32 sm:h-40 lg:h-48 p-3 sm:p-4 border border-gray-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
        />
        {!isRevealed && (
          <button
            onClick={() => onAIGrading(question)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center gap-2"
          >
            <Bot className="w-4 h-4" />AI评分
          </button>
        )}
        {aiExplanations[question.id] && (
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-2 text-purple-700">
              <Bot className="w-4 h-4" />
              <span className="font-medium">AI评分结果</span>
            </div>
            <div className="text-gray-700 text-sm whitespace-pre-wrap">{aiExplanations[question.id]}</div>
          </div>
        )}
      </div>
    );
  }

  // 选择题（单选/多选/判断）
  const currentOptionScale = (() => {
    const imgOpts = question?.options?.filter(o => o.image) || [];
    if (imgOpts.length === 0) return 100;
    const firstWidth = imgOpts[0].imageWidth ?? 100;
    const allSame = imgOpts.every(o => (o.imageWidth ?? 100) === firstWidth);
    return allSame ? firstWidth : 100;
  })();

  return (
    <>
      {isPreview && (
        <div className="flex items-center gap-1 mb-3">
          <button
            onClick={() => updateOptionLayout('grid')}
            className={`p-2 rounded-lg transition-colors ${((question as any)?.optionLayout || 'grid') === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            title="网格布局"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="8" height="8" rx="1" />
              <rect x="13" y="3" width="8" height="8" rx="1" />
              <rect x="3" y="13" width="8" height="8" rx="1" />
              <rect x="13" y="13" width="8" height="8" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => updateOptionLayout('list')}
            className={`p-2 rounded-lg transition-colors ${((question as any)?.optionLayout || 'grid') === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            title="列表布局"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="4" rx="1" />
              <rect x="3" y="10" width="18" height="4" rx="1" />
              <rect x="3" y="16" width="18" height="4" rx="1" />
            </svg>
          </button>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          {[50, 75, 100].map(scale => (
            <button
              key={scale}
              onClick={() => {
                const newOptions = question.options.map(o =>
                  o.image ? { ...o, imageWidth: scale } : o
                );
                if (onExamQuestionsChange) {
                  const newQuestions = examQuestions.map(q =>
                    q.id === question.id ? { ...q, options: newOptions } : q
                  );
                  onExamQuestionsChange(newQuestions);
                  if (onHasWidthChangesChange) onHasWidthChangesChange(true);
                }
              }}
              className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                currentOptionScale === scale
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={`缩放${scale}%`}
            >
              {scale}%
            </button>
          ))}
        </div>
      )}
      <div className={`grid gap-3 ${((question as any)?.optionLayout || 'grid') === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {question?.options?.map((option) => {
          const isSelected = question.type === 'multi_choice'
            ? (answers[question.id] || '').split(',').includes(option.id)
            : answers[question.id] === option.id;
          const showCorrect = isRevealed && option.isCorrect;
          const showWrong = isRevealed && isSelected && !option.isCorrect;

          return (
            <button
              key={option.id}
              onClick={() => !isRevealed && handleAnswer(option.id)}
              disabled={isRevealed}
              className={`w-full p-3 sm:p-4 rounded-lg border-2 text-left transition-all duration-200 flex items-start gap-2 sm:gap-3 ${
                showCorrect
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : showWrong
                  ? 'border-red-500 bg-red-50 shadow-sm'
                  : isSelected
                  ? 'border-blue-500 bg-blue-100 shadow-md ring-2 ring-blue-200'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <span className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-medium transition-all duration-200 ${
                showCorrect
                  ? 'border-green-500 bg-green-500 text-white'
                  : showWrong
                  ? 'border-red-500 bg-red-500 text-white'
                  : isSelected
                  ? 'border-blue-500 bg-blue-500 text-white shadow-md'
                  : 'border-gray-300 text-gray-500 bg-white'
              }`}>
                {option.id}
              </span>
              <div className="flex-1 text-gray-800">
                {option.text?.startsWith('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: option.text }} />
                ) : (
                  <span>{option.text}</span>
                )}
                {option.image && (
                  <div className="mt-2">
                    <img
                      src={option.image.startsWith('/sb-api/') ? option.image : option.image.startsWith('/storage/') ? `${getSupabaseUrl()}${option.image}` : option.image}
                      alt={`选项${option.id}图片`}
                      className="rounded-lg border border-gray-200"
                      style={{ width: `${option.imageWidth ?? 100}%`, maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                      loading="lazy"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {isPreview && (
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-xs text-gray-500">缩放:</span>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          step="1"
                          value={option.imageWidth ?? 100}
                          onChange={(e) => updateOptionImageWidth(option.id, parseInt(e.target.value))}
                          className="w-20 h-1 accent-blue-500 cursor-pointer"
                        />
                        <input
                          type="number"
                          min="10"
                          max="100"
                          value={option.imageWidth ?? 100}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (!isNaN(val) && val >= 10 && val <= 100) {
                              updateOptionImageWidth(option.id, val);
                            }
                          }}
                          className="w-16 h-5 text-sm text-center border border-gray-300 rounded focus:border-blue-500 outline-none"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {showCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
              {showWrong && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {isRevealed && question && (
        <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">正确答案：</span>
                  <span className="font-medium text-green-600">
                    {question.options?.filter(o => o.isCorrect).map(o => o.id).join(', ') || '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">你的答案：</span>
                  <span className={`font-medium ${(() => {
                    const userAns = answers[question.id];
                    if (!userAns) return 'text-gray-400';
                    const correctIds = question.options?.filter(o => o.isCorrect).map(o => o.id) || [];
                    const userIds = userAns.split(',');
                    const isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
                    return isCorrect ? 'text-green-600' : 'text-red-600';
                  })()}`}>
                    {answers[question.id] || '未作答'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowReportModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
              >
                <Flag className="w-3.5 h-3.5" />
                我要纠错
              </button>
            </div>
            {question.explanation && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-600" />答案解析
                </h4>
                <div className="text-gray-700 leading-relaxed max-h-40 overflow-y-auto">{question.explanation}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 纠错弹窗 */}
      <ReportErrorModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        questionId={question?.id || ''}
        questionTitle={question?.title || ''}
        paperId={paperId}
        currentUser={currentUser}
      />
    </>
  );
}